<template>
  <button
    ref="trigger"
    type="button"
    aria-haspopup="true"
    aria-expanded="false"
    v-tooltip.top="languageStore.t(item.label)"
    :aria-label="languageStore.t(item.label)"
    class="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-md transition-colors hover:bg-surface-100 dark:hover:bg-surface-800"
    @mouseenter="openFlyout"
    @mouseleave="scheduleHide"
    @click="openFlyout"
    @keydown.esc="hideFlyout"
  >
    <Icons :icon="item.icon" class="shrink-0 font-bold" />
  </button>

  <TieredMenu
    ref="flyout"
    :model="item.items"
    popup
    :pt="{ root: { onMouseenter: cancelHide, onMouseleave: scheduleHide } }"
    @show="setOpenState(true)"
    @hide="setOpenState(false)"
  >
    <template #item="{ item: entry, props, hasSubmenu }">
      <router-link v-if="entry.to" v-slot="{ href, navigate }" :to="entry.to" custom>
        <a :href="href" v-bind="props.action" class="flex items-center gap-2" @click="navigate">
          <Icons :icon="entry.icon" class="shrink-0 font-bold" />
          <span class="font-bold">{{ languageStore.t(entry.label as string) }}</span>
        </a>
      </router-link>

      <a v-else v-bind="props.action" class="flex items-center justify-between gap-2">
        <span class="flex items-center gap-2">
          <Icons :icon="entry.icon" class="shrink-0 font-bold" />
          <span class="font-bold">{{ languageStore.t(entry.label as string) }}</span>
        </span>
        <Icons v-if="hasSubmenu" icon="chevron-right" class="shrink-0 font-bold" />
      </a>
    </template>
  </TieredMenu>
</template>

<script setup lang="ts">
import Icons from '@/components/subcomponents/Icons.vue'
import { INavBarItem } from '@/models/navbar'
import { useLanguageStore } from '@/stores/LanguageStore'
import { TieredMenu } from 'primevue'
import { onUnmounted, ref } from 'vue'

const HIDE_DELAY = 200
const OPEN_CLASSES = ['bg-[var(--p-primary-100)]', 'dark:bg-[var(--p-primary-800)]']

let closeOpenedFlyout: (() => void) | null = null

const { item } = defineProps<{ item: INavBarItem }>()

const languageStore = useLanguageStore()

const flyout = ref<InstanceType<typeof TieredMenu> | null>(null)
const trigger = ref<HTMLButtonElement | null>(null)

let hideTimer: ReturnType<typeof setTimeout> | undefined

const setOpenState = (open: boolean) => {
  const element = trigger.value

  if (!element) {
    return
  }

  element.setAttribute('aria-expanded', String(open))
  OPEN_CLASSES.forEach((openClass) => element.classList.toggle(openClass, open))
}

const hideFlyout = () => {
  flyout.value?.hide()
  if (closeOpenedFlyout === hideFlyout) {
    closeOpenedFlyout = null
  }
}

const cancelHide = () => {
  clearTimeout(hideTimer)
  hideTimer = undefined
}

const scheduleHide = () => {
  cancelHide()
  hideTimer = setTimeout(hideFlyout, HIDE_DELAY)
}

const enableSubmenuHover = () => {
  const menu = flyout.value as unknown as { dirty?: boolean } | null

  if (menu) {
    menu.dirty = true
  }
}

const openFlyout = (event: Event) => {
  cancelHide()

  if (closeOpenedFlyout && closeOpenedFlyout !== hideFlyout) {
    closeOpenedFlyout()
  }

  closeOpenedFlyout = hideFlyout
  flyout.value?.show(event)
  enableSubmenuHover()
}

onUnmounted(() => {
  cancelHide()
  if (closeOpenedFlyout === hideFlyout) {
    closeOpenedFlyout = null
  }
})
</script>
